import React, { useState, useEffect } from 'react';
import { Plus, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    due_date: '',
    assigned_to: ''
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await api.get('/tasks');
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await api.put(`/tasks/${editingTask.id}`, formData);
        toast.success('Task updated');
      } else {
        await api.post('/tasks', formData);
        toast.success('Task created');
      }
      setShowForm(false);
      setEditingTask(null);
      setFormData({ title: '', description: '', status: 'todo', priority: 'medium', due_date: '', assigned_to: '' });
      fetchTasks();
    } catch (error) {
      toast.error('Failed to save task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      await api.put(`/tasks/${taskId}`, { ...task, status: newStatus });
      fetchTasks();
      toast.success('Task status updated');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const statusGroups = {
    todo: tasks.filter(t => t.status === 'todo'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    completed: tasks.filter(t => t.status === 'completed')
  };

  const getPriorityColor = (priority) => {
    return priority === 'high' ? 'text-red-400' : priority === 'medium' ? 'text-white' : 'text-blue-400';
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-white">Loading...</div></div>;
  }

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">Tasks</h1>
              <p className="text-lg text-zinc-300">{tasks.length} total tasks</p>
            </div>
            <button
              onClick={() => { setShowForm(true); setEditingTask(null); }}
              data-testid="add-task-button"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-6 py-3 transition-all duration-200"
            >
              <Plus className="w-5 h-5" strokeWidth={2} />
              Add Task
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {showForm && (
          <div className="glass-card p-6 mb-6">
            <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4">{editingTask ? 'Edit Task' : 'New Task'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <input
                    type="text"
                    required
                    placeholder="Task title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <textarea
                    rows="3"
                    placeholder="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none"
                  />
                </div>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none"
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                </select>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none"
                />
              </div>
              <div className="flex gap-3">
                <button type="submit" className="bg-white text-black hover:bg-zinc-200 font-bold uppercase px-6 py-2 rounded-sm">Save</button>
                <button type="button" onClick={() => { setShowForm(false); setEditingTask(null); }} className="bg-white/10 text-white hover:bg-white/20 px-6 py-2 rounded-sm">Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {Object.entries({ todo: 'To Do', in_progress: 'In Progress', completed: 'Completed' }).map(([status, label]) => (
            <div key={status}>
              <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                {status === 'completed' && <CheckCircle className="w-5 h-5 text-green-400" />}
                {status === 'in_progress' && <Clock className="w-5 h-5 text-white" />}
                {status === 'todo' && <AlertCircle className="w-5 h-5 text-blue-400" />}
                {label} ({statusGroups[status].length})
              </h3>
              <div className="space-y-3">
                {statusGroups[status].map(task => (
                  <div key={task.id} className="glass-card p-4 card-hover">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-white">{task.title}</h4>
                      <span className={`text-xs uppercase font-bold ${getPriorityColor(task.priority)}`}>{task.priority}</span>
                    </div>
                    {task.description && <p className="text-sm text-zinc-400 mb-3">{task.description}</p>}
                    {task.due_date && <p className="text-xs text-zinc-500 mb-3">Due: {task.due_date}</p>}
                    <div className="flex gap-2">
                      {status !== 'completed' && (
                        <button onClick={() => handleStatusChange(task.id, status === 'todo' ? 'in_progress' : 'completed')} className="text-xs bg-white text-black px-3 py-1 rounded-sm">                          {status === 'todo' ? 'Start' : 'Complete'}
                        </button>
                      )}
                      <button onClick={() => { setEditingTask(task); setFormData(task); setShowForm(true); }} className="text-xs bg-white/10 text-white px-3 py-1 rounded-sm">Edit</button>
                      <button onClick={() => handleDelete(task.id)} className="text-xs bg-red-500/20 text-red-400 px-3 py-1 rounded-sm">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Tasks;